import { css } from '@emotion/react';
import {
  useEffect,
  useMemo, useState,
} from 'react';
import {
  type DropdownOption, RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import {
  ScrollBarComponent, ScrollbarType,
} from '~/_shared/components/scrollbar/scrollbar.component';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import type { ExportReportTemplateSelectorArea } from '~/exportReport/templateSelector/exportReportTemplateSelector.constants';
import {
  getAgsReportCountryForArea,
  getAreaReportItems, getReportAreaTranslation,
} from '~/exportReport/templateSelector/exportReportTemplateSelector.helpers';
import { useExportReport } from '~/exportReport/useExportReport';
import { type ModalProps } from '~/modal/modalType.enum';
import { modalContentStyle } from '~/proximity/edit/proximityEdit.styles';

export type ExportReportTemplateSelectorModalProps = ModalProps<{
  reportAreas: ReadonlyArray<ExportReportTemplateSelectorArea>;
}>;

const contentWrapperStyle = css({
  padding: '16px 24px 24px',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 16,
  '@media all and (min-width: 800px)': {
    gridTemplateColumns: 'repeat(3, minmax(200px, 1fr))',
  },
  '@media all and (min-width: 1100px)': {
    gridTemplateColumns: 'repeat(4, minmax(200px, 1fr))',
  },
});

const itemWrapperStyle = (theme: Theme) => css({
  background: theme.backgroundColors.secondary,
  transition: 'background-color 0.2s',
  border: `1px solid ${theme.borderColors.primary}`,
  textAlign: 'left',
  padding: 12,
  borderRadius: 4,
  minHeight: 125,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'stretch',
  '&:hover': {
    background: theme.backgroundColors.secondaryHover,
  },
});

const iconStyle = css({
  fontSize: '24px',
  width: 'auto',
});

const titleStyle = css({
  fontSize: 16,
  fontWeight: 400,
  marginTop: 6,
});

const descriptionStyle = (theme: Theme) => css({
  color: theme.textColors.secondary,
  fontSize: 12,
  fontWeight: 400,
  marginTop: 2,
});

const dropdownWrapperStyle = css({
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  padding: '24px 24px 0',
});

const dropdownStyle = css({
  minWidth: 250,
});

const dropdownTriggerStyle = css({
  height: 32,
});

export const ExportReportTemplateSelectorModalComponent = ({ onClose, isOpen, reportAreas }: ExportReportTemplateSelectorModalProps) => {
  const [selectedArea, setSelectedArea] = useState<ExportReportTemplateSelectorArea | null>(null);
  const [t] = useTranslation();

  const items = useMemo(() => {
    if (!selectedArea) {
      return [];
    }

    return getAreaReportItems(selectedArea);
  }, [selectedArea]);

  const areaDropdownOptions: ReadonlyArray<DropdownOption<ExportReportTemplateSelectorArea>> = useMemo(() => {
    return reportAreas.map(area => ({
      value: area,
      name: getReportAreaTranslation(area, t),
    }));
  }, [reportAreas, t]);

  const { setReportSettings } = useExportReport();

  useEffect(() => {
    setSelectedArea(reportAreas[0] ?? null);
  }, [reportAreas]);

  if (!selectedArea) {
    return null;
  }

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      caption={t('Choose Summary report')}
      contentStyle={modalContentStyle}
      maxWidth={976}
    >
      {areaDropdownOptions.length > 1 && (
        <div css={dropdownWrapperStyle}>
          <span>{t('Data Source Country')}</span>

          <RegularDropdownComponent
            css={dropdownStyle}
            triggerStyle={dropdownTriggerStyle}
            value={selectedArea}
            onChange={setSelectedArea}
            options={areaDropdownOptions}
          />
        </div>
      )}

      <ScrollBarComponent type={ScrollbarType.Horizontal}>
        <div css={contentWrapperStyle}>
          {items.map(item => (
            <button
              css={itemWrapperStyle}
              key={item.id}
              onClick={() => setReportSettings(item.template, getAgsReportCountryForArea(selectedArea))}
            >
              <div css={iconStyle}>
                <FontAwesomeIcon icon={item.icon} />
              </div>
              <div css={titleStyle}>
                {t(item.title)}
              </div>
              <div css={descriptionStyle}>
                {t(item.description)}
              </div>
            </button>
          ))}
        </div>
      </ScrollBarComponent>
    </ModalComponent>
  );
};
